import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useToast } from "@/hooks/use-toast";
import i18n from "@/i18n";
import type { StayPackageConfigValue } from "@/lib/validations/stayPackage";

type StayPackageConfigRow = Database["public"]["Tables"]["stay_package_config"]["Row"];

export interface StayPackageConfig {
  id: string;
  catalogPackageId: string;
  appliesToModule: string;
  packageKind: string;
  period: string | null;
  daysPerWeek: number | null;
  includesOvernight: boolean;
  sizeRestriction: string | null;
}

export function normalizeStayPackageConfig(row: Pick<StayPackageConfigRow, "id" | "catalog_package_id" | "applies_to_module" | "package_kind" | "period" | "days_per_week" | "includes_overnight" | "size_restriction">): StayPackageConfig {
  return {
    id: row.id,
    catalogPackageId: row.catalog_package_id,
    appliesToModule: row.applies_to_module,
    packageKind: row.package_kind,
    period: row.period,
    daysPerWeek: row.days_per_week,
    includesOvernight: row.includes_overnight,
    sizeRestriction: row.size_restriction,
  };
}

export function useStayPackageConfig(catalogPackageId?: string | null) {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["stay-package-config", customerId, catalogPackageId],
    enabled: Boolean(customerId && catalogPackageId),
    queryFn: async (): Promise<StayPackageConfig | null> => {
      if (!customerId || !catalogPackageId) return null;

      const { data, error } = await supabase
        .from("stay_package_config")
        .select("*")
        .eq("catalog_package_id", catalogPackageId)
        .maybeSingle();

      if (error) throw error;
      return data ? normalizeStayPackageConfig(data) : null;
    },
  });
}

export interface UpsertStayPackageConfigInput {
  catalogPackageId: string;
  config: StayPackageConfigValue;
}

export function useUpsertStayPackageConfig() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: customerId } = useCurrentClinicCustomerId();

  return useMutation({
    mutationFn: async ({ catalogPackageId, config }: UpsertStayPackageConfigInput) => {
      if (!customerId) {
        throw new Error(i18n.t("erp:phase8.catalogPackages.errors.noClinicSelected"));
      }

      const { data, error } = await supabase
        .from("stay_package_config")
        .upsert({
          catalog_package_id: catalogPackageId,
          applies_to_module: config.appliesToModule,
          package_kind: config.packageKind,
          period: config.period,
          days_per_week: config.daysPerWeek,
          includes_overnight: false,
          size_restriction: config.sizeRestriction,
        }, { onConflict: "catalog_package_id" })
        .select("*")
        .single();

      if (error) throw error;
      return data ? normalizeStayPackageConfig(data) : null;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["stay-package-config", customerId, data?.catalogPackageId] });
      queryClient.invalidateQueries({ queryKey: ["stay-commercial-packages"] });
      queryClient.invalidateQueries({ queryKey: ["catalog-package-template"] });
    },
    onError: (error: Error) => {
      toast({
        title: i18n.t("erp:phase8.catalogPackages.saveErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
